import React, { useRef, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { GridApi, GridReadyEvent } from 'ag-grid-community';
import { useQuery } from '@tanstack/react-query';
import { blockchain } from 'src/api/blockchain';
import { useAddressAndChainParams } from 'src/hooks/routing/useAddressAndChainParams';
import { tokenHoldingsColDefs } from './aggrid/columnDefs';

export const TokenHoldingsTable = () => {
  const gridApiRef = useRef<GridApi | null>(null);
  const { chain, address } = useAddressAndChainParams();

  const {
    data: { data: { tokens = [] } = {} } = {},
    isLoading,
    error,
  } = useQuery({
    queryKey: ['evmTokensBalance', chain, address],
    queryFn: () => blockchain.getEVMTokensBalance(chain, address),
    enabled: !!chain && !!address,
  });

  const onGridReady = useCallback((e: GridReadyEvent) => {
    gridApiRef.current = e.api;
  }, []);

  // Memoize default column definitions for performance
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: false,
      suppressMovable: true, // Disable column moving for performance
      filter: true, // Enable filtering for all columns
      floatingFilter: true, // Show filter inputs below headers
      filterParams: {
        debounceMs: 300, // Debounce filter input for performance
      },
    }),
    [],
  );

  // Memoize grid options for performance
  const gridOptions = useMemo(
    () => ({
      // Row virtualization settings
      rowBuffer: 10, // Keep 10 rows outside viewport for smooth scrolling
      rowSelection: 'single' as const,

      // Performance optimizations
      suppressCellFocus: true,
      suppressRowClickSelection: true,
      suppressRowHoverHighlight: false,
      suppressAnimationFrame: false,
      animateRows: false, // Disable row animations

      // Pagination for large datasets
      pagination: true,
      paginationPageSize: 100, // Show 100 rows per page
      paginationAutoPageSize: false,

      // Height settings
      rowHeight: 60,
      headerHeight: 40,
      floatingFiltersHeight: 35, // Height for filter row

      // Overlays
      overlayLoadingTemplate: `<span class="ag-overlay-loading-center">Loading tokens...</span>`,
      overlayNoRowsTemplate: `<span class="ag-overlay-no-rows-center">No token holdings found</span>`,

      // Scrolling optimizations
      suppressHorizontalScroll: false,
      alwaysShowHorizontalScroll: false,
      suppressScrollOnNewData: true,

      // Disable features not needed for performance
      suppressMenuHide: true,
      suppressRowDeselection: true,

      // Filter options
      suppressMenuFilterPanel: false, // Allow menu filter panel
      suppressMenuMainPanel: false,
      suppressMenuColumnPanel: true, // Hide column panel to reduce complexity
    }),
    [],
  );

  if (!chain || !address) {
    return <div className="p-4 text-red-600">Missing chain or address</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-red-600">Error: {(error as Error).message}</div>
    );
  }

  return (
    <div className="ag-theme-alpine w-full" style={{ height: '600px' }}>
      <AgGridReact
        rowData={tokens}
        columnDefs={tokenHoldingsColDefs}
        defaultColDef={defaultColDef}
        gridOptions={gridOptions}
        onGridReady={onGridReady}
        loading={isLoading}
      />
    </div>
  );
};
